// The reports adapter (plan §4.8): pages ask for a named report over a window and get the metrics layer's answer
// (`@radish/metrics` QueryResult: rows of grouped bigint figures plus server totals), or "prepare a download", or
// "busy". No report math runs in the browser. The contract source calls the generated client's `reports.*` queries; a
// report with no contract yet answers `unavailable` and its page says so.
import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DrawerSalesFigures, DrawerSummary, QueryResult } from '@radish/metrics';
import type { Client } from '@radish/api-client';
import { unwrap, useApi, type CallFailure, type CallResult } from '../../data/api.tsx';
import { uuidv7 } from '../../lib/ids.ts';

/** A register drawer or a server's own bank. */
export type TillKind = 'register' | 'server_bank';

export type ReportName =
  | 'sales_overview' | 'sales_by_day' | 'item_sales' | 'item_sales_variants' | 'category_sales' | 'department_sales' | 'dining_option_sales' | 'employee_sales' | 'payments' | 'payroll'
  | 'time_segment_sales' | 'dashboard_top_items' | 'item_names' | 'payments_by_tender_type';

/** The Item Sales filters (legacy Category, Department, Employee); empty lists are left out. */
/** Item Sales' filters: legacy's Filter by device, Category, Department and Employees (each sent only when non-empty). */
export type ItemFilters = Partial<Record<'device' | 'category' | 'department' | 'employee' | 'dining_option', readonly string[]>>;

/** A `metrics.query` request: catalog metric ids, up to three group dimensions, member filters. */
export interface MetricsInput {
  readonly metrics: readonly string[];
  readonly groupBy: readonly string[];
  readonly window: ReportWindowInput;
  readonly filters?: Readonly<Record<string, readonly string[]>>;
}

/** The dimension a report's chart is drawn over. */
export type TrendDimension = 'product' | 'category' | 'department' | 'employee' | 'dining_option';

/** Business dates (inclusive), optional weekdays and wall-clock windows in minutes (may wrap past midnight). */
export interface ReportWindowInput {
  readonly locationIds: readonly string[];
  readonly from: string;
  readonly to: string;
  readonly weekdays?: { readonly days: readonly number[]; readonly basis: 'business' | 'calendar' };
  readonly timeOfDay?: readonly { readonly start: number; readonly end: number }[];
  /** One drawer's orders and refunds only (the Drawer detail). */
  readonly tillSessionId?: string;
  /** Only these drawers' orders and refunds (the End of Day report's closed drawers, legacy summary3). */
  readonly tillSessionIds?: readonly string[];
  /** The order states the sales figures count (the Dashboard's Order Status); absent = closed orders. */
  readonly orderStates?: readonly ('closed' | 'open')[];
}

export type ReportAnswer =
  | { readonly kind: 'ok'; readonly result: QueryResult; readonly freshness: string | null }
  | { readonly kind: 'requires_export'; readonly reason: 'detail_over_366_days' | 'statement_timeout' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'unavailable' };

export type DrawerAnswer = { readonly kind: 'ok'; readonly summary: DrawerSummary } | { readonly kind: 'unavailable' };

/** A payment taken for one of a drawer's orders (All Transactions › Payments). */
export interface DrawerPayment {
  readonly tenderId: string; readonly orderId: string; readonly orderNumber: string | null; readonly businessDate: string; readonly localMinute: number;
  readonly method: string; readonly amount: bigint; readonly tip: bigint;
  /** Legacy's tender ticket ("A-42"); absent from a server that predates it. */
  readonly ticket?: string | undefined;
  /** B1477: the card's last four digits (legacy `cc_last_four`); null when none was recorded, absent from an older server. */
  readonly cardLast4?: string | null | undefined;
  /** B1850: when the payment itself was taken (its calendar date and minute); absent from an older server or fact. */
  readonly takenCalendarDate?: string | undefined;
  readonly takenLocalMinute?: number | undefined;
}

/** A drawer's paid in, paid out, cash drop, tip payout, gratuity payout or (imported) tip payout from the safe (the Drawer detail's Cash Management). */
export interface DrawerCashManagementLine {
  readonly movementId: string; readonly purpose: 'paid_in' | 'paid_out' | 'drop' | 'tip_payout' | 'gratuity_payout' | 'tip_payout_safe_withdrawal'; readonly amount: bigint;
  readonly employeeId: string | null; readonly recipientEmployeeId: string | null; readonly note: string | null; readonly at: string;
}

/** One settled part of a server bank's closeout: into a register drawer (letter and device) or the safe, and who approved it. */
export interface BankSettlementPart {
  readonly destination: 'cash_box' | 'deposit'; readonly drawerLetter: string | null; readonly deviceId: string | null; readonly approvedByEmployeeId: string | null;
}

/** A server bank's closeout as legacy's Closeout Summary reads it (ServerBankSettlementStatus). */
export interface BankCloseout {
  readonly cash: 'unpaid' | 'collected' | 'paid'; readonly tips: 'unpaid' | 'paid'; readonly combined: boolean; readonly approvedAt: string | null;
  readonly cashPart: BankSettlementPart | null; readonly tipPart: BankSettlementPart | null;
  /** Cash collected less tips and gratuity as submitted (legacy `close_out`); absent from a server that predates it. */
  readonly closeoutCash?: bigint | undefined;
}

/**
 * One row of a register drawer's Settlements (Server Banks) (legacy CashDrawerSettelements.vue): a server bank's closeout
 * settled into it. `amount` signed (into the drawer positive); the bank's figures quote in the Description.
 */
export interface BankSettlementLine {
  readonly bankTillSessionId: string; readonly serverEmployeeId: string | null; readonly at: string;
  readonly kind: 'settled_in' | 'settled_out' | 'tip_payout' | 'gratuity_payout'; readonly amount: bigint; readonly combined: boolean;
  readonly cashCollected: bigint; readonly nonCashTips: bigint; readonly gratuity: bigint;
}

/** One drawer (the Drawer detail): its details, Order Summary, Cash Summary lines and payments, every figure the server's. */
export type DrawerDetailAnswer =
  | {
    readonly kind: 'ok'; readonly session: Omit<DrawerSummary['sessions'][number], 'locationId'>;
    readonly counts: { readonly orders: bigint; readonly voids: bigint; readonly refunds: bigint };
    readonly payments: readonly DrawerPayment[];
    /** Absent from a server that predates it. */
    readonly cashManagement?: readonly DrawerCashManagementLine[] | undefined;
    /** A server bank's Closeout Summary (REP-020); null on a register drawer or a bank not submitted; absent from an older server. */
    readonly bankCloseout?: BankCloseout | null | undefined;
    /** A register drawer's Settlements (Server Banks), oldest first; absent from an older server. */
    readonly bankSettlements?: readonly BankSettlementLine[] | undefined;
  }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unavailable' };

/**
 * One drawer's Summary of Receipts (`reports.drawerReceipts`, B1654): legacy summary4's receipt lines, every figure the
 * server's. `card` is Card (Exp. Deposit) when `trueMargin`.
 */
export type DrawerReceiptsAnswer =
  | {
    readonly kind: 'ok'; readonly trueMargin: boolean;
    readonly card: bigint; readonly cash: bigint; readonly checks: bigint; readonly giftCard: bigint; readonly houseAccount: bigint;
    readonly tenderTypes: readonly { readonly name: string; readonly amount: bigint }[]; readonly total: bigint;
  }
  | { readonly kind: 'not_found' };

/**
 * One drawer's DRAWER SALES SUMMARY (`reports.drawerSalesSummary`, B1671): legacy summary4's sales figures, every one the
 * server's (metrics `drawerSalesStatement` lays them out).
 */
export type DrawerSalesSummaryAnswer =
  | ({ readonly kind: 'ok'; readonly trueMargin: boolean } & DrawerSalesFigures)
  | { readonly kind: 'not_found' }
  | { readonly kind: 'requires_export' };

interface AdjustmentPlace {
  readonly orderId: string;
  readonly orderNumber: string | null;
  readonly businessDate: string;
  readonly calendarDate: string;
  readonly localMinute: number;
  readonly employeeId: string;
  readonly reason: string | null;
}
export type VoidRow = AdjustmentPlace & {
  readonly voidExtent: 'full_order' | 'payment_only'; readonly paymentVoided: boolean; readonly voidPayment: bigint; readonly voidOrder: bigint;
  /** Its item lines (legacy `items_count`); absent from an older server. */
  readonly itemsCount?: bigint | undefined;
};
export type DeletionRow = AdjustmentPlace & { readonly voidKey: string; readonly itemName: string; readonly qtyMilli: bigint; readonly itemAmount: bigint };

/** The Adjustments report (R17): Voids and Deletions with the server's totals. */
export type AdjustmentsAnswer =
  | {
    readonly kind: 'ok'; readonly voids: readonly VoidRow[]; readonly deletions: readonly DeletionRow[];
    readonly totals: { readonly voidPayment: bigint; readonly voidOrder: bigint; readonly itemAmount: bigint };
  }
  | { readonly kind: 'requires_export'; readonly reason: 'detail_over_366_days' | 'statement_timeout' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'unavailable' };

/** The Discounts report: one row per discount name with how often it was applied and what it took off, and the Total. */
export type DiscountsAnswer =
  | {
    readonly kind: 'ok'; readonly rows: readonly { readonly name: string; readonly applied: bigint; readonly amount: bigint }[];
    readonly totals: { readonly applied: bigint; readonly amount: bigint };
  }
  | { readonly kind: 'requires_export'; readonly reason: 'detail_over_366_days' | 'statement_timeout' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'unavailable' };

/** Tips paid in by source over a window (the Overview's "Tip paid in (source)" lines) and their total. */
export type TipsPaidInAnswer =
  | { readonly kind: 'ok'; readonly sources: readonly { readonly name: string; readonly amount: bigint }[]; readonly total: bigint }
  | { readonly kind: 'busy' }
  | { readonly kind: 'unavailable' };

/** One Refunds row: a refund on its own date with what its payments gave back. */
export interface RefundRow {
  readonly refundId: string; readonly orderId: string; readonly orderNumber: string | null; readonly businessDate: string; readonly calendarDate: string;
  readonly localMinute: number; readonly employeeId: string; readonly comments: string | null; readonly itemsMilli: bigint; readonly orderRefund: bigint;
  readonly tipRefund: bigint; readonly total: bigint;
}

/** One refund's Refund Details slide-over (the server's figures). */
interface RefundDetail {
  readonly refundId: string; readonly orderId: string; readonly orderNumber: string | null; readonly calendarDate: string; readonly localMinute: number;
  readonly employeeId: string; readonly tabName: string | null; readonly diningOptionId: string | null;
  readonly lines: readonly { readonly name: string; readonly qtyMilli: bigint; readonly net: bigint }[];
  readonly itemsNet: bigint; readonly tax: bigint; readonly amount: bigint;
  readonly cashDiscountAdj: bigint; readonly cashDiscountAdjShown: boolean; readonly splitShown: boolean; readonly customAmountLine: bigint | null;
  readonly payments: readonly { readonly ticket: string; readonly method: string; readonly amountPaid: bigint; readonly refundAmount: bigint; readonly refundTip: bigint }[];
}

export type RefundDetailAnswer = { readonly kind: 'ok'; readonly refund: RefundDetail } | { readonly kind: 'not_found' } | { readonly kind: 'unavailable' };

/** The Refunds report with the server's totals. */
export type RefundsAnswer =
  | { readonly kind: 'ok'; readonly refunds: readonly RefundRow[]; readonly totals: { readonly orderRefund: bigint; readonly tipRefund: bigint; readonly total: bigint } }
  | { readonly kind: 'requires_export'; readonly reason: 'detail_over_366_days' | 'statement_timeout' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'unavailable' };

/** A closed order's row (the drawer's All Transactions › Orders): the server's figures; each row adds up (B4 FIX). */
export interface OrderRow {
  readonly orderId: string; readonly orderNumber: string | null; readonly businessDate: string; readonly localMinute: number; readonly employeeId: string;
  /** B1848: the calendar date of the order's open time; absent from an older server or fact. */
  readonly calendarDate?: string | undefined;
  readonly subtotal: bigint; readonly discounts: bigint; readonly cashDiscount: bigint; readonly gratuity: bigint; readonly tax: bigint; readonly tips: bigint; readonly total: bigint;
  /** The Employee Sales breakdown's Discounts and Total: what the order charged (legacy OrderResource). */
  readonly storedDiscounts: bigint; readonly storedTotal: bigint;
  /** A drawer's Orders tab's Discounts as legacy lists them (retaken off the subtotal; B4 / L117); absent from an older server. */
  readonly listedDiscounts?: bigint | undefined;
  /** The Employee Sales breakdown's Cash Discount (True Margin; voided payments' fees counted as legacy, B644); absent from an older server. */
  readonly breakdownCashDiscount?: bigint | undefined;
  /** The Employee Sales breakdown's Subtotal, legacy's `orders.subtotal` with gift-card loads (B1686); absent from an older server. */
  readonly storedSubtotal?: bigint | undefined;
  /** A drawer's Orders tab's Cash Discount as legacy lists it, a load's share of the fees too (B1733); absent from an older server. */
  readonly listedCashDiscount?: bigint | undefined;
  /** Still open: the drawer's Orders tab offers Force Complete on it (DLG-073). */
  readonly open?: boolean;
}

export type OrderListAnswer =
  | { readonly kind: 'ok'; readonly orders: readonly OrderRow[] }
  | { readonly kind: 'requires_export'; readonly reason: 'detail_over_366_days' | 'statement_timeout' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'unavailable' };

export type ExportState = { readonly exportId: string; readonly state: 'queued' | 'running' | 'done' | 'failed' } | { readonly kind: 'unavailable' };

/** A deleted category or department and its last name. */
interface DeletedName { readonly id: string; readonly name: string }
/** B1777: a custom tender type (deleted ones too) by id and name. */
export interface TenderTypeName { readonly id: string; readonly name: string }
export interface DeletedNames { readonly categories: readonly DeletedName[]; readonly departments: readonly DeletedName[]; readonly diningOptions: readonly DeletedName[] }

export interface ReportsSource {
  /** The same source, its retries of a `busy` answer stopped when `signal` aborts (the query's own, B1384). */
  withSignal?(signal: AbortSignal): ReportsSource;
  run(name: ReportName, window: ReportWindowInput, filters?: ItemFilters): Promise<ReportAnswer>;
  /** Gross and net per business date for each member of the dimension (the report charts). */
  trend?(dimension: TrendDimension, window: ReportWindowInput, filters?: ItemFilters): Promise<ReportAnswer>;
  /** `tillKind`: register drawers or server banks only (the Drawers/Banks tabs); absent = every till. */
  drawers(window: ReportWindowInput, tillKind?: TillKind): Promise<DrawerAnswer>;
  /** Any catalog metrics, grouped and filtered (`metrics.query`): the drill-in dialogs. */
  query?(input: MetricsInput): Promise<ReportAnswer>;
  /** Business dates with a drawer before `before`, newest first (the EOD Report list). */
  eodDates?(locationId: string, before: string): Promise<readonly string[] | null>;
  /** The location's deleted categories and departments with their last names (reports name history sold under them). */
  deletedNames?(locationId: string): Promise<DeletedNames>;
  /** B1777: the location's custom tender types, deleted ones too, in legacy's order (the Payment Summary's rows). */
  tenderTypes?(locationId: string): Promise<readonly TenderTypeName[]>;
  requestExport(name: ReportName, locationId: string, from: string, to: string): Promise<ExportState>;
  /** Voided orders, voided payments and line voids of the window (Adjustments). */
  adjustments?(window: ReportWindowInput): Promise<AdjustmentsAnswer>;
  /** Each discount and comp of the window's closed orders, by name (Discounts). */
  discounts?(window: ReportWindowInput): Promise<DiscountsAnswer>;
  /** The window's tips paid in by source (Overview). */
  tipsPaidIn?(window: ReportWindowInput): Promise<TipsPaidInAnswer>;
  /** Every refund of the window on its own date (Refunds). */
  refunds?(window: ReportWindowInput): Promise<RefundsAnswer>;
  /** One refund's Refund Details. */
  refundDetail?(locationId: string, refundId: string): Promise<RefundDetailAnswer>;
  /** One drawer (Drawer detail). */
  drawerDetail?(locationId: string, tillSessionId: string): Promise<DrawerDetailAnswer>;
  /** One drawer's Summary of Receipts (its Breakdown). */
  drawerReceipts?(locationId: string, tillSessionId: string): Promise<DrawerReceiptsAnswer>;
  /** One drawer's DRAWER SALES SUMMARY (its Breakdown). */
  drawerSalesSummary?(locationId: string, tillSessionId: string): Promise<DrawerSalesSummaryAnswer>;
  /** The closed orders of a window (a drawer's, with `tillSessionId`; one employee's, with `employeeId`). */
  orderList?(window: ReportWindowInput, employeeId?: string, includeOpen?: boolean): Promise<OrderListAnswer>;
}

const NAMED = {
  sales_overview: (c: Client) => c.queries.reports.salesOverview,
  sales_by_day: (c: Client) => c.queries.reports.salesByDay,
  item_sales: (c: Client) => c.queries.reports.itemSales,
  item_sales_variants: (c: Client) => c.queries.reports.itemSalesVariants,
  category_sales: (c: Client) => c.queries.reports.categorySales,
  department_sales: (c: Client) => c.queries.reports.departmentSales,
  dining_option_sales: (c: Client) => c.queries.reports.diningOptionSales,
  employee_sales: (c: Client) => c.queries.reports.employeeSales,
  payments: (c: Client) => c.queries.reports.payments,
  payroll: (c: Client) => c.queries.reports.payroll,
  time_segment_sales: (c: Client) => c.queries.reports.timeSegmentSales,
  dashboard_top_items: (c: Client) => c.queries.reports.dashboardTopItems,
  item_names: (c: Client) => c.queries.reports.itemNames,
  payments_by_tender_type: (c: Client) => c.queries.reports.paymentsByTenderType,
} as const;

/**
 * The client's own waits before each retry of a `busy` answer (B1384), 4 to 5 s in all with jitter. Each of the three
 * attempts may also wait up to the server's 8 s for a slot and then run its statement (up to 5 s interactive), so under a
 * true overload the busy banner shows after about 3 x 8 s + 5 s = 29 s, and a query that gets its slot only at the end of
 * the last wait and runs to its statement timeout answers after about 29 s + 5 s = 34 s. The server's wait does the real
 * work; these few retries are a safety net.
 */
export const BUSY_RETRY_MS: readonly number[] = [1_000, 3_000];

/** Waits `ms`, or rejects with the signal's reason as soon as it aborts. */
function pause(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(signal.reason);
      return;
    }
    const stop = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', stop);
      resolve();
    }, ms);
    signal?.addEventListener('abort', stop, { once: true });
  });
}

/**
 * Asks again while the server answers `busy` (every slot of the account taken and its wait run out), after each of
 * `waits`, a little jitter added so a page's queries don't return together; the last answer stands, busy or not (B1384).
 * Stops (rejecting with the signal's reason) once `signal` aborts: a page left or refiltered asks no more.
 */
export async function untilFree<T>(call: () => Promise<T>, waits: readonly number[] = BUSY_RETRY_MS, signal?: AbortSignal, sleep: (ms: number, signal?: AbortSignal) => Promise<void> = pause): Promise<T> {
  let answer = await call();
  for (const wait of waits) {
    if ((answer as { readonly kind?: unknown } | null)?.kind !== 'busy') return answer;
    await sleep(wait + Math.floor(Math.random() * (wait / 4)), signal);
    if (signal?.aborted === true) throw signal.reason;
    answer = await call();
  }
  return answer;
}

/**
 * The generated client's `reports.*` queries (the metrics layer on the server's report pool) and `reports.requestExport`.
 * Drawers wait for a till-session report contract (`cash.tillSessions`), so they answer `unavailable` for now.
 */
function contractReportsSource(client: Client, signalled: (signal: AbortSignal) => Client, signal?: AbortSignal): ReportsSource {
  const ask = <T,>(call: () => Promise<CallResult<T>>): Promise<T> => untilFree(() => unwrap(call), BUSY_RETRY_MS, signal);
  return {
    // The query's signal stops both the retries and the request in flight (#2335 review).
    withSignal: (s) => contractReportsSource(signalled(s), signalled, s),
    async run(name, window, filters) {
      const input = { window: window as never, ...(hasFilters(filters) ? { filters: filters as never } : {}) };
      const answer = await ask(() => NAMED[name](client)(input as never));
      return answer.kind === 'ok' ? { kind: 'ok', result: answer.result, freshness: answer.freshness } : answer;
    },
    async trend(dimension, window, filters) {
      const answer = await ask(() => client.queries.reports.salesTrend({ window: window as never, dimension, ...(hasFilters(filters) ? { filters: filters as never } : {}) }));
      return answer.kind === 'ok' ? { kind: 'ok', result: answer.result, freshness: answer.freshness } : answer;
    },
    async drawers(window, tillKind) {
      const a = await ask(() => client.queries.reports.drawers({ window: window as never, ...(tillKind === undefined ? {} : { tillKind }) }));
      if (a.kind !== 'ok') return { kind: 'unavailable' };
      const sessions = a.sessions.map((s) => ({ ...s, locationId: window.locationIds[0] ?? '' }));
      const deposit: DrawerSummary['deposit'] = a.deposit === null
        ? { available: false, reason: 'uncounted_sessions', uncounted: sessions.filter((s) => s.counted === null).map((s) => ({ tillSessionId: s.tillSessionId, expected: s.expected, aboveOpening: s.expected - s.openingFloat })) }
        : { available: true, deposit: a.deposit, drops: a.drops };
      return { kind: 'ok', summary: { sessions, openingFloat: a.openingFloat, cashSales: a.cashSales, expectedCash: a.expectedCash, actualCloseoutCash: a.actualCloseoutCash, overShort: a.overShort, deposit } };
    },
    async query(input) {
      const answer = await ask(() => client.queries.metrics.query(input as never));
      return answer.kind === 'ok' ? { kind: 'ok', result: answer.result, freshness: answer.freshness } : answer;
    },
    async eodDates(locationId, before) {
      const a = await ask(() => client.queries.reports.eodDates({ locationId: locationId as never, before }));
      return a.dates;
    },
    async deletedNames(locationId) {
      return ask(() => client.queries.reports.deletedNames({ locationId: locationId as never }));
    },
    async tenderTypes(locationId) {
      return (await ask(() => client.queries.reports.tenderTypes({ locationId: locationId as never }))).tenderTypes;
    },
    async adjustments(window) {
      return ask(() => client.queries.reports.adjustments({ window: window as never }));
    },
    async discounts(window) {
      return ask(() => client.queries.reports.discounts({ window: window as never }));
    },
    async refunds(window) {
      return ask(() => client.queries.reports.refunds({ window: window as never }));
    },
    async tipsPaidIn(window) {
      return ask(() => client.queries.reports.tipsPaidIn({ window: window as never }));
    },
    async drawerDetail(locationId, tillSessionId) {
      return ask(() => client.queries.reports.drawerDetail({ locationId: locationId as never, tillSessionId }));
    },
    async drawerReceipts(locationId, tillSessionId) {
      return ask(() => client.queries.reports.drawerReceipts({ locationId: locationId as never, tillSessionId }));
    },
    async drawerSalesSummary(locationId, tillSessionId) {
      return ask(() => client.queries.reports.drawerSalesSummary({ locationId: locationId as never, tillSessionId }));
    },
    async orderList(window, employeeId, includeOpen) {
      return ask(() => client.queries.reports.orderList({ window: window as never, ...(employeeId === undefined ? {} : { employeeId: employeeId as never }), ...(includeOpen === true ? { includeOpen: true } : {}) }));
    },
    async refundDetail(locationId, refundId) {
      return ask(() => client.queries.reports.refundDetail({ locationId: locationId as never, refundId }));
    },
    async requestExport(name, locationId, from, to) {
      // Time Segment Sales is not an export job (its page downloads the table it shows); the Dashboard has no download, Item Sales' variant rows ride with Item Sales, and the tender type rows with the Overview.
      if (name === 'time_segment_sales' || name === 'dashboard_top_items' || name === 'item_sales_variants' || name === 'item_names' || name === 'payments_by_tender_type') return { kind: 'unavailable' };
      const commandId = uuidv7();
      const result = await unwrap(() => client.commands.reports.requestExport({ reportName: name, locationId: locationId as never, from, to }, { commandId }));
      return { exportId: result.exportId, state: result.state };
    },
  };
}

const hasFilters = (f: ItemFilters | undefined): f is ItemFilters => f !== undefined && Object.values(f).some((v) => v !== undefined && v.length > 0);

/** The filters with empty lists left out (so an unfiltered page and its cache key stay the same). */
const compact = (f: ItemFilters | undefined): ItemFilters | undefined =>
  hasFilters(f) ? Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v.length > 0)) : undefined;

const Context = createContext<ReportsSource | null>(null);

export function ReportsSourceProvider({ source, children }: { readonly source: ReportsSource; readonly children: ReactNode }) {
  return <Context.Provider value={source}>{children}</Context.Provider>;
}

/** The source with its busy retries tied to a query's signal, when it has any. */
const bound = (source: ReportsSource, signal: AbortSignal): ReportsSource => source.withSignal?.(signal) ?? source;

export function useReportsSource(): ReportsSource {
  const { client, signalled } = useApi();
  return useContext(Context) ?? contractReportsSource(client, signalled);
}

/** One fetch per window change (Q-RPT-01 FIX), keyed under the location like every other read. */
export function useReport(name: ReportName, window: ReportWindowInput, filters?: ItemFilters, enabled = true) {
  const source = useReportsSource();
  const { environment } = useApi();
  const f = compact(filters);
  return useQuery<ReportAnswer, CallFailure>({
    queryKey: [environment.profile, window.locationIds[0] ?? '', `reports.${name}`, window, f ?? null],
    queryFn: ({ signal }) => bound(source, signal).run(name, window, f),
    enabled,
  });
}

/** A report's chart rows (none on a single day, where the chart is a top-five bar from the report's own rows). */
export function useTrend(dimension: TrendDimension, window: ReportWindowInput, filters?: ItemFilters) {
  const source = useReportsSource();
  const { environment } = useApi();
  const f = compact(filters);
  return useQuery<ReportAnswer, CallFailure>({
    queryKey: [environment.profile, window.locationIds[0] ?? '', 'reports.salesTrend', dimension, window, f ?? null],
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.trend === undefined ? Promise.resolve({ kind: 'unavailable' as const }) : s.trend(dimension, window, f); },
    enabled: window.from !== window.to,
  });
}

export function useDrawers(window: ReportWindowInput, tillKind?: TillKind) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<DrawerAnswer, CallFailure>({
    queryKey: [environment.profile, window.locationIds[0] ?? '', 'cash.tillSessions', window, tillKind ?? null],
    queryFn: ({ signal }) => bound(source, signal).drawers(window, tillKind),
  });
}

/** The Adjustments report of a window (both tabs read the one answer). */
export function useAdjustments(window: ReportWindowInput) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<AdjustmentsAnswer, CallFailure>({
    queryKey: [environment.profile, window.locationIds[0] ?? '', 'reports.adjustments', window],
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.adjustments === undefined ? Promise.resolve({ kind: 'unavailable' as const }) : s.adjustments(window); },
  });
}

const NO_DELETED: DeletedNames = { categories: [], departments: [], diningOptions: [] };

/** The location's deleted categories and departments by id, with their last names (none when the source has no list). */
export function useDeletedNames(locationId: string) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<DeletedNames, CallFailure>({
    queryKey: [environment.profile, locationId, 'reports.deletedNames'],
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.deletedNames === undefined ? Promise.resolve(NO_DELETED) : s.deletedNames(locationId); },
  });
}

/** B1777: the location's custom tender types, deleted ones too, in legacy's order; null when the source has no list. */
export function useTenderTypes(locationId: string) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<readonly TenderTypeName[] | null, CallFailure>({
    queryKey: [environment.profile, locationId, 'reports.tenderTypes'],
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.tenderTypes === undefined ? Promise.resolve(null) : s.tenderTypes(locationId); },
  });
}

/** The window's tips paid in by source (the Overview's and the EOD's "Tip paid in (source)" lines). */
export function useTipsPaidIn(window: ReportWindowInput) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<TipsPaidInAnswer, CallFailure>({
    queryKey: [environment.profile, window.locationIds[0] ?? '', 'reports.tipsPaidIn', window],
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.tipsPaidIn === undefined ? Promise.resolve({ kind: 'unavailable' as const }) : s.tipsPaidIn(window); },
  });
}

/** The Discounts report of a window. */
export function useDiscounts(window: ReportWindowInput) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<DiscountsAnswer, CallFailure>({
    queryKey: [environment.profile, window.locationIds[0] ?? '', 'reports.discounts', window],
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.discounts === undefined ? Promise.resolve({ kind: 'unavailable' as const }) : s.discounts(window); },
  });
}

/** The Refunds report of a window. */
export function useRefunds(window: ReportWindowInput) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<RefundsAnswer, CallFailure>({
    queryKey: [environment.profile, window.locationIds[0] ?? '', 'reports.refunds', window],
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.refunds === undefined ? Promise.resolve({ kind: 'unavailable' as const }) : s.refunds(window); },
  });
}

/** One refund's Refund Details (fetched when the slide-over opens). */
export function useRefundDetail(locationId: string, refundId: string | null) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<RefundDetailAnswer, CallFailure>({
    queryKey: [environment.profile, locationId, 'reports.refundDetail', refundId],
    enabled: refundId !== null,
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.refundDetail === undefined || refundId === null ? Promise.resolve({ kind: 'unavailable' as const }) : s.refundDetail(locationId, refundId); },
  });
}

/** One drawer's details and Cash Summary. */
export function useDrawerDetail(locationId: string, tillSessionId: string) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<DrawerDetailAnswer, CallFailure>({
    queryKey: [environment.profile, locationId, 'reports.drawerDetail', tillSessionId],
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.drawerDetail === undefined ? Promise.resolve({ kind: 'unavailable' as const }) : s.drawerDetail(locationId, tillSessionId); },
  });
}

/** The closed orders of a window (a drawer's Orders tab; one employee's, the Employee Sales breakdown). */
export function useOrderList(window: ReportWindowInput, enabled = true, employeeId?: string, includeOpen = false) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<OrderListAnswer, CallFailure>({
    queryKey: [environment.profile, window.locationIds[0] ?? '', 'reports.orderList', window, employeeId ?? null, includeOpen],
    enabled,
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.orderList === undefined ? Promise.resolve({ kind: 'unavailable' as const }) : s.orderList(window, employeeId, includeOpen); },
  });
}

/** Any catalog metrics, grouped and filtered (`metrics.query`): the drill-in dialogs. */
export function useMetrics(input: MetricsInput, enabled = true) {
  const source = useReportsSource();
  const { environment } = useApi();
  return useQuery<ReportAnswer, CallFailure>({
    queryKey: [environment.profile, input.window.locationIds[0] ?? '', 'metrics.query', input],
    enabled,
    queryFn: ({ signal }) => { const s = bound(source, signal); return s.query === undefined ? Promise.resolve({ kind: 'unavailable' as const }) : s.query(input); },
  });
}
